import type { TFunction } from "i18next";
import type { FieldPath, FieldValues, UseFormSetError } from "react-hook-form";
import { toast } from "@erp/ui";
import { ApiError, NetworkError } from "@/lib/api/errors";
import { isProblem } from "@/lib/api/problem";
import type { Problem } from "@/lib/api/problem";

/**
 * Convert a wire field path to an RHF dot path:
 * `"lines[2].qty"` → `"lines.2.qty"`.
 */
export function wireFieldToFormPath(field: string): string {
  return field.replace(/\[(\d+)\]/g, ".$1");
}

export interface ApplyProblemToFormOptions<TFieldValues extends FieldValues> {
  /**
   * Wire field name → form field path, for fields whose form name differs
   * from the API payload name (e.g. `{ display_name: "displayName" }`).
   * Mapped entries are always treated as present in the form.
   */
  fieldMap?: Record<string, FieldPath<TFieldValues>>;
  /**
   * Top-level field names registered in the form (`Object.keys(getValues())`).
   * Field errors whose path root is not listed are reported back via
   * `unmappedGlobal` instead of being set on the form. When omitted, every
   * field error is applied blindly.
   */
  knownFields?: readonly string[];
  /** Focus the first field that receives an error (default true). */
  shouldFocus?: boolean;
}

export interface ApplyProblemResult {
  /**
   * Server message of the first field error that could not be attached to a
   * form field, or null when everything mapped. Surface it globally (see
   * `surfaceProblem`) so the user is never left with a silent failure.
   */
  unmappedGlobal: string | null;
}

/**
 * Map an RFC 9457 problem's `errors[]` onto react-hook-form field errors.
 * The first error per field wins (matching RHF's firstError criteria mode)
 * and errors are set with `type: "server"` so they clear on revalidation.
 */
export function applyProblemToForm<TFieldValues extends FieldValues>(
  problem: Problem,
  setError: UseFormSetError<TFieldValues>,
  options: ApplyProblemToFormOptions<TFieldValues> = {},
): ApplyProblemResult {
  const { fieldMap, knownFields, shouldFocus = true } = options;
  const applied = new Set<string>();
  let unmappedGlobal: string | null = null;

  for (const fieldError of problem.errors ?? []) {
    const mappedName = fieldMap?.[fieldError.field];
    const name = mappedName ?? wireFieldToFormPath(fieldError.field);
    const root = name.split(".")[0] ?? name;
    const inForm =
      mappedName !== undefined || knownFields === undefined || knownFields.includes(root);
    if (!inForm) {
      unmappedGlobal ??= fieldError.message;
      continue;
    }
    if (applied.has(name)) {
      continue;
    }
    applied.add(name);
    setError(
      // Runtime string from the wire; membership was checked against knownFields.
      name as FieldPath<TFieldValues>,
      { type: "server", message: fieldError.message },
      { shouldFocus: shouldFocus && applied.size === 1 },
    );
  }

  return { unmappedGlobal };
}

export interface SurfaceProblemOptions {
  /**
   * `unmappedGlobal` returned by `applyProblemToForm` — the leftover field
   * message that needs a global surface because no form field could carry it.
   */
  unmappedGlobal?: string | null;
}

/**
 * The ONE error-surfacing convention:
 * - field-level problems (`errors[]`) belong on the form — this function stays
 *   silent for them unless an `unmappedGlobal` leftover is passed in;
 * - global problems raise a destructive toast (title from `problem.title` or
 *   the generic error, description from `detail`);
 * - 403 renders the forbidden message with the missing permission code
 *   interpolated when `problem.code` carries it;
 * - network failures get the offline message.
 *
 * Accepts a `Problem`, an `ApiError` (whose `problem` is used), or any
 * unknown thrown value (generic toast).
 */
export function surfaceProblem(
  input: unknown,
  t: TFunction,
  options: SurfaceProblemOptions = {},
): void {
  if (input instanceof NetworkError) {
    toast({ variant: "destructive", title: t("errors.network") });
    return;
  }

  const problem = resolveProblem(input);
  const status = problem?.status ?? (input instanceof ApiError ? input.status : undefined);

  if (status === 403) {
    toast({
      variant: "destructive",
      title: problem?.code
        ? t("errors.forbidden", { permission: problem.code })
        : problem?.title || t("errors.generic"),
      description: problem?.detail,
    });
    return;
  }

  if (problem?.errors !== undefined && problem.errors.length > 0) {
    // Field-level problem: the form shows the details.
    if (options.unmappedGlobal) {
      toast({
        variant: "destructive",
        title: problem.title || t("errors.generic"),
        description: options.unmappedGlobal,
      });
    }
    return;
  }

  toast({
    variant: "destructive",
    title: problem?.title || t("errors.generic"),
    description: problem?.detail,
  });
}

function resolveProblem(input: unknown): Problem | undefined {
  if (isProblem(input)) {
    return input;
  }
  if (input instanceof ApiError) {
    return input.problem;
  }
  return undefined;
}
